/**
 * Learn more about deep linking with React Navigation
 * https://reactnavigation.org/docs/deep-linking
 * https://reactnavigation.org/docs/configuring-links
 */

import { Platform } from 'react-native';
import { LinkingOptions, getStateFromPath } from '@react-navigation/native';
import * as Linking from 'expo-linking';

import { AuthStackParamList } from '@pletly/feature-auth';
import { LinkingConfiguration as OrganisationLinking } from '@pletly/feature-organisation';
import { RootStackParamList } from './types';

const Communities = {
	path: '/communities',
	exact: true,
	screens: {
		Your: 'your',
		Explore: 'explore',
	},
};

const linking: LinkingOptions<AuthStackParamList & RootStackParamList> = {
	prefixes: [Linking.createURL('/')],
	config: {
		screens: {
			Login: { path: '/login' },
			Register: { path: '/register' },
			RegisterWithLink: { path: '/register/:code' },
			Home: {
				path: '/',
				exact: true,
				screens: {
					Feed: 'Feed',
					...Platform.select({
						native: {
							Connections: 'Connections',
							Communities,
							Search: 'Search',
							Menu: 'Menu',
						},
					}),
				},
			},
			ForgotPassword: {
				path: '/forgot-password',
			},
			ResetPassword: {
				path: '/auth/passwordReset',
			},
			SetFirstPassword: {
				path: '/auth/set-password',
			},
			NewPost: 'posts/new',
			NewPostPrivacy: 'posts/new/privacy',
			PostComments: 'posts/:postUuid',
			SharePost: 'posts/:postUuid/share',
			EditPost: 'posts/:postUuid/edit',
			ScheduledPosts: 'posts/scheduled',
			ConnectUser: 'ConnectUser',
			CreateAccount: 'CreateAccount',
			...Platform.select({
				web: {
					Connections: 'Connections',
				},
			}),
			Chats: 'Chats',
			Notifications: 'Notifications',
			Conversation: 'Conversation',
			Modal: 'modal',
			About: 'About',
			Onboarding: {
				path: '/onboarding',
				screens: {
					Welcome: '/',
					Names: '/names',
					Telephone: '/telephone',
					Connections: '/connections',
				},
			},
			Profile: {
				path: '/profile/:userId',
			},
			CreateInformationFolder: {
				path: '/profile/:userId/information-folder/new/:parentFolderId?',
				exact: true,
			},
			InformationFolder: {
				path: '/profile/:userId/information-folder/:id',
			},
			EditInformationFolder: {
				path: '/profile/:userId/information-folder/:id/edit',
			},
			InformationFolderAccess: {
				path: '/profile/:userId/information-folder/:folderId/access',
			},
			CreateInformationFeed: {
				path: '/profile/:userId/information-folder/:folderId/feed/new',
				exact: true,
			},
			InformationFeed: {
				path: '/profile/:userId/information-folder/:folderId/feed/:feedId',
			},
			EditInformationFeed: {
				path: '/profile/:userId/information-folder/:folderId/feed/:id/edit',
			},
			InformationFeedAccess: {
				path: '/profile/:userId/information-folder/:folderId/feed/:feedId/access',
			},
			InformationFeedPost: {
				path: '/profile/:userId/information-folder/:folderId/feed/:feedId/:postUuid',
			},
			NewInformationFeedPost: {
				path: '/profile/:userId/information-folder/:folderId/feed/:feedId/new',
			},
			EditInformationFeedPost: {
				path: '/profile/:userId/information-folder/:folderId/feed/:feedId/:postUuid/edit',
			},
			EditProfile: 'EditProfile',
			EditProfileInfo: 'EditProfileInfo',
			InviteAsCaregiverFor: {
				path: '/profile/invite-as-caregiver-for',
			},
			FillInformation: 'FillInformation',
			FillInformationConfirmation: 'FillInformationConfirmation',

			...Platform.select({
				web: {
					Communities,
					Files: 'files',
				},
			}),
			NewCommunity: 'communities/create',
			Community: {
				path: 'communities/:uuid',
				initialRouteName: 'CommunityView',
				screens: {
					CommunityView: '',
					CommunityMembers: 'members',
					CommunityInvite: 'invite',
					NewCommunityPost: 'posts/new',
					EditCommunityPost: 'posts/:postUuid/edit',
					CommunityPost: 'posts/:postUuid',
				},
			},
			...OrganisationLinking,
			PostsToReview: 'posts/review',
			PostsSubmittedForReview: 'posts/submitted',
			EditPostSubmittedForReview: 'posts/submitted/:postUuid/edit',
			NotFound: '*',
		},
	},
	getStateFromPath: (path, options) => {
		// It's not possible to pass params to the initial screen through the URL.
		// https://reactnavigation.org/docs/configuring-links/#rendering-an-initial-route
		const state = getStateFromPath(path, options);
		const [lastRoute] = state?.routes || [];

		if (lastRoute?.name === 'Community' && lastRoute.state?.routes) {
			lastRoute.state = {
				...lastRoute.state,
				routes: lastRoute.state?.routes.map((route) => {
					return { ...route, params: { ...lastRoute.params, ...route.params } };
				}),
			};
		}

		return state;
	},
};

export default linking;
